import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon, AlertCircleIcon, ClockIcon, ArrowRightIcon, CalendarIcon, AlertTriangleIcon, MoreVerticalIcon, Edit2Icon, Trash2Icon, CheckIcon, FileTextIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../providers/ThemeProvider';
import { getTeacherTasks, Task, updateTask, deleteTask } from '../../services/taskService';
import { getTaskDisplayStatus, getTaskStatusBadge } from '../tasks/taskUtils';
import { TaskDetailsPanel } from '../tasks/TaskDetailsPanel';
import { DeleteTaskModal } from '../DeleteTaskModal';
import { toast } from 'react-hot-toast';
export const ActionItemsSection = () => {
  const {
    currentUser
  } = useAuth();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // State for 3-dot menu and task panel
  const [openMenuTaskId, setOpenMenuTaskId] = useState<string | null>(null);
  const [selectedTaskForPanel, setSelectedTaskForPanel] = useState<Task | null>(null);
  const [isTaskPanelOpen, setIsTaskPanelOpen] = useState(false);
  const [isTaskEditMode, setIsTaskEditMode] = useState(false);
  // State for delete confirmation modal
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  useEffect(() => {
    const fetchTasks = async () => {
      if (!currentUser?.uid) return;
      try {
        setLoading(true);
        setError(null);
        // Fetch all tasks for the teacher
        const allTasks = await getTeacherTasks(currentUser.uid);
        // Filter out completed tasks
        const activeTasks = allTasks.filter(task => task.status !== 'completed');
        // Process and prioritize tasks
        const prioritizedTasks = prioritizeTasks(activeTasks);
        // Take top 5
        setTasks(prioritizedTasks.slice(0, 5));
      } catch (err) {
        console.error('Error fetching action items:', err);
        setError('Failed to load action items');
      } finally {
        setLoading(false);
      }
    };
    fetchTasks();
    // Listen for task updates to refresh the list
    const handleTaskUpdate = () => fetchTasks();
    window.addEventListener('taskCreated', handleTaskUpdate);
    window.addEventListener('taskUpdated', handleTaskUpdate);
    window.addEventListener('taskDeleted', handleTaskUpdate);
    return () => {
      window.removeEventListener('taskCreated', handleTaskUpdate);
      window.removeEventListener('taskUpdated', handleTaskUpdate);
      window.removeEventListener('taskDeleted', handleTaskUpdate);
    };
  }, [currentUser]);
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is outside the menu and button
      const target = event.target as HTMLElement;
      const isMenuClick = target.closest('[data-menu-dropdown]');
      const isButtonClick = target.closest('[data-menu-button]');
      if (!isMenuClick && !isButtonClick && openMenuTaskId) {
        setOpenMenuTaskId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openMenuTaskId]);
  // Helper to prioritize tasks
  const prioritizeTasks = (tasks: Task[]): Task[] => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const todayTime = now.getTime();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowTime = tomorrow.getTime();
    const nextWeek = new Date(now);
    nextWeek.setDate(nextWeek.getDate() + 7);
    const nextWeekTime = nextWeek.getTime();
    const overdue: Task[] = [];
    const dueToday: Task[] = [];
    const dueTomorrow: Task[] = [];
    const dueSoon: Task[] = []; // Within 7 days
    const others: Task[] = [];
    tasks.forEach(task => {
      const dueDateStr = task.dueDate || task.deadline || task.dueAt;
      if (!dueDateStr) {
        others.push(task);
        return;
      }
      const dueDate = new Date(dueDateStr);
      dueDate.setHours(0, 0, 0, 0);
      const dueTime = dueDate.getTime();
      if (dueTime < todayTime) {
        overdue.push(task);
      } else if (dueTime === todayTime) {
        dueToday.push(task);
      } else if (dueTime === tomorrowTime) {
        dueTomorrow.push(task);
      } else if (dueTime <= nextWeekTime) {
        dueSoon.push(task);
      } else {
        others.push(task);
      }
    });
    // Sort within buckets
    // Overdue: oldest first (most urgent)
    overdue.sort((a, b) => {
      const dateA = new Date(a.dueDate || a.deadline || a.dueAt || 0).getTime();
      const dateB = new Date(b.dueDate || b.deadline || b.dueAt || 0).getTime();
      return dateA - dateB;
    });
    // Due Soon: soonest first
    dueSoon.sort((a, b) => {
      const dateA = new Date(a.dueDate || a.deadline || a.dueAt || 0).getTime();
      const dateB = new Date(b.dueDate || b.deadline || b.dueAt || 0).getTime();
      return dateA - dateB;
    });
    // Others: most recently created first
    others.sort((a, b) => {
      const dateA = new Date(a.createdAt || 0).getTime();
      const dateB = new Date(b.createdAt || 0).getTime();
      return dateB - dateA;
    });
    return [...overdue, ...dueToday, ...dueTomorrow, ...dueSoon, ...others];
  };
  // Format due date for display
  const formatDueDate = (dateStr?: string | null) => {
    if (!dateStr) return 'No due date';
    const date = new Date(dateStr);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const taskDate = new Date(date);
    taskDate.setHours(0, 0, 0, 0);
    const diffTime = taskDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return <>
          <span className="text-red-500 font-medium" data-id="element-1100">
            Overdue by {Math.abs(diffDays)} days
          </span>
        </>;
    if (diffDays === 0) return <>
          <span className="text-orange-500 font-medium" data-id="element-1101">Due Today</span>
        </>;
    if (diffDays === 1) return <>
          <span className="text-orange-500 font-medium" data-id="element-1102">Due Tomorrow</span>
        </>;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  // Task menu handlers
  const toggleTaskMenu = (taskId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (openMenuTaskId === taskId) {
      setOpenMenuTaskId(null);
    } else {
      setOpenMenuTaskId(taskId);
    }
  };
  const handleEditTask = (task: Task) => {
    setOpenMenuTaskId(null);
    setSelectedTaskForPanel(task);
    setIsTaskPanelOpen(true);
    setIsTaskEditMode(false);
  };
  const handleMarkComplete = async (task: Task, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuTaskId(null);
    await handleToggleComplete(task, e);
  };
  const handleDeleteTaskClick = (task: Task, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuTaskId(null);
    setTaskToDelete(task);
    setIsDeleteModalOpen(true);
  };
  const handleConfirmDelete = async () => {
    if (!taskToDelete || !currentUser?.uid || !taskToDelete.studentId) {
      toast.error('Cannot delete task: missing information');
      return;
    }
    try {
      await deleteTask(currentUser.uid, taskToDelete.studentId, taskToDelete.id);
      // Update local state
      setTasks(tasks.filter(t => t.id !== taskToDelete.id));
      // Dispatch event to notify other components
      window.dispatchEvent(new Event('taskDeleted'));
      toast.success('Task deleted successfully');
      setIsDeleteModalOpen(false);
      setTaskToDelete(null);
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };
  // Task panel handlers
  const closeTaskPanel = () => {
    setIsTaskPanelOpen(false);
    setIsTaskEditMode(false);
    setTimeout(() => {
      setSelectedTaskForPanel(null);
    }, 300);
  };
  const handleTaskEditClick = () => {
    setIsTaskEditMode(true);
  };
  const handleTaskExitEditMode = () => {
    setIsTaskEditMode(false);
  };
  const handleTaskDelete = async (taskId: string) => {
    if (!currentUser?.uid || !selectedTaskForPanel?.studentId) {
      toast.error('Cannot delete task: missing information');
      return;
    }
    try {
      await deleteTask(currentUser.uid, selectedTaskForPanel.studentId, taskId);
      // Update local state
      setTasks(tasks.filter(t => t.id !== taskId));
      // Dispatch event
      window.dispatchEvent(new Event('taskDeleted'));
      toast.success('Task deleted successfully');
      closeTaskPanel();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };
  const handleTaskSave = async (taskId: string, updates: any) => {
    if (!currentUser?.uid || !selectedTaskForPanel?.studentId) {
      toast.error('Cannot update task: missing information');
      return;
    }
    try {
      const updateData: any = {
        title: updates.title,
        status: updates.status,
        notes: updates.notes || '',
        reference: updates.reference || ''
      };
      if (updates.dueAt) {
        updateData.dueDate = updates.dueAt;
      }
      if (updates.startDate) {
        updateData.startDate = updates.startDate;
      }
      await updateTask(currentUser.uid, selectedTaskForPanel.studentId, taskId, updateData);
      // Update local state
      const updatedTaskData = {
        ...selectedTaskForPanel,
        id: taskId,
        name: updates.title,
        title: updates.title,
        deadline: updates.dueAt || selectedTaskForPanel.deadline,
        dueDate: updates.dueAt || selectedTaskForPanel.dueDate,
        startDate: updates.startDate || selectedTaskForPanel.startDate,
        notes: updates.notes || '',
        status: updates.status,
        reference: updates.reference || ''
      };
      setTasks(tasks.map(t => t.id === taskId ? updatedTaskData : t));
      setSelectedTaskForPanel(updatedTaskData);
      // Dispatch event
      window.dispatchEvent(new Event('taskUpdated'));
      toast.success('Task updated successfully');
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };
  // Function to toggle task completion status
  const handleToggleComplete = async (task: Task, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent any parent click handlers
    if (!currentUser?.uid || !task.studentId) {
      toast.error('Cannot update task: missing information');
      return;
    }
    const isCurrentlyCompleted = task.status === 'completed';
    const newStatus = isCurrentlyCompleted ? 'pending' : 'completed';
    const completedAt = isCurrentlyCompleted ? null : new Date().toISOString();
    // Store the task data needed for undo (avoid closure issues)
    const taskId = task.id;
    const studentId = task.studentId;
    const previousStatus = task.status;
    const previousCompletedAt = task.completedAt;
    // Optimistic update - update UI immediately
    const updatedTasks = tasks.map(t => t.id === task.id ? {
      ...t,
      status: newStatus,
      completedAt
    } : t);
    setTasks(updatedTasks);
    try {
      // Update in Firestore
      await updateTask(currentUser.uid, task.studentId, task.id, {
        status: newStatus,
        completedAt
      });
      // Dispatch event to notify other components
      window.dispatchEvent(new Event('taskUpdated'));
      // Show undo toast ONLY when marking as completed
      if (newStatus === 'completed') {
        toast.success(t => <div className="flex items-center justify-between gap-3" data-id="element-1103">
              <span data-id="element-1104">Task marked as completed</span>
              <button onClick={async () => {
            toast.dismiss(t.id);
            // FIXED: Use functional state update to avoid stale closure
            setTasks(currentTasks => {
              return currentTasks.map(task_item => task_item.id === taskId ? {
                ...task_item,
                status: previousStatus,
                completedAt: previousCompletedAt
              } : task_item);
            });
            try {
              // Update Firestore with reverted state
              await updateTask(currentUser.uid, studentId, taskId, {
                status: previousStatus,
                completedAt: previousCompletedAt
              });
              // Dispatch event
              window.dispatchEvent(new Event('taskUpdated'));
              toast.success('Task restored');
            } catch (error) {
              console.error('Error undoing task completion:', error);
              toast.error('Failed to undo');
              // Revert back to completed state on error
              setTasks(currentTasks => {
                return currentTasks.map(task_item => task_item.id === taskId ? {
                  ...task_item,
                  status: 'completed',
                  completedAt
                } : task_item);
              });
            }
          }} className="px-3 py-1 bg-white text-gray-800 rounded-md font-medium hover:bg-gray-100 transition-colors" data-id="element-1105">
                Undo
              </button>
            </div>, {
          duration: 6000,
          style: {
            minWidth: '300px'
          }
        });
      } else {
        // Simple message when marking as incomplete
        toast.success('Task marked as incomplete');
      }
    } catch (error) {
      console.error('Error updating task completion:', error);
      // Revert optimistic update on error
      setTasks(tasks);
      toast.error('Failed to update task');
    }
  };
  if (!currentUser) return null;
  // Loading State
  if (loading) {
    return <>
        <div className={`mb-6 rounded-xl shadow-sm p-6 ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-1106">
          <div className="flex items-center justify-between mb-4" data-id="element-1107">
            <div className={`h-6 w-32 rounded animate-pulse ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-1108"></div>
            <div className={`h-4 w-20 rounded animate-pulse ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-1109"></div>
          </div>
          <div className="space-y-3" data-id="element-1110">
            {[1, 2, 3].map(i => <div key={i} className={`h-16 rounded-lg animate-pulse ${isDark ? 'bg-navy-700' : 'bg-gray-50'}`} data-id="element-1111"></div>)}
          </div>
        </div>
      </>;
  }
  // Empty State (don't show section if no tasks need attention)
  if (tasks.length === 0 && !error) {
    return <>
        <div className={`mb-6 rounded-xl shadow-sm p-6 ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-1112">
          <div className="flex items-center justify-between mb-4" data-id="element-1113">
            <h2 className={`text-lg font-semibold flex items-center ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-1114">
              <AlertCircleIcon className="h-5 w-5 mr-2" style={{
              color: '#3B82F6'
            }} data-id="element-1115" />
              Assigned Tasks
            </h2>
            <Link to="/assigned-tasks" className="text-sm text-[#10B981] hover:text-[#059669] font-medium flex items-center transition-colors" data-id="element-1116">
              View All Tasks
              <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-1117" />
            </Link>
          </div>
          <div className={`p-4 rounded-lg border ${isDark ? 'bg-navy-700/50 border-navy-600' : 'bg-white border-gray-100'}`} data-id="element-1118">
            <div className="flex items-start gap-3" data-id="element-1119">
              <CheckCircleIcon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-navy-500' : 'text-gray-400'}`} data-id="element-1120" />
              <div data-id="element-1121">
                <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-1122">
                  No tasks need your attention right now.
                </p>
                <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-1123">
                  You're all caught up!
                </p>
              </div>
            </div>
          </div>
        </div>
      </>;
  }
  return <>
      <div className={`mb-6 rounded-xl shadow-sm ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-1124">
        <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-800' : 'border-gray-200'} flex items-center justify-between`} data-id="element-1125">
          <h2 className={`text-lg font-semibold flex items-center ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-1126">
            <AlertCircleIcon className="h-5 w-5 mr-2" style={{
            color: '#3B82F6'
          }} data-id="element-1127" />
            Assigned Tasks
          </h2>
          <Link to="/assigned-tasks" className="text-sm text-[#10B981] hover:text-[#059669] font-medium flex items-center transition-colors" data-id="element-1128">
            View All Tasks
            <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-1129" />
          </Link>
        </div>

        <div className="p-4" data-id="element-1130">
          {error ? <>
              <div className={`p-4 rounded-lg flex items-center ${isDark ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-600'}`} data-id="element-1131">
                <AlertTriangleIcon className="h-5 w-5 mr-3 flex-shrink-0" data-id="element-1132" />
                <p className="text-sm" data-id="element-1133">{error}</p>
              </div>
            </> : <div className="space-y-3" data-id="element-1134">
              {tasks.map(task => <div key={task.id} onClick={() => handleEditTask(task)} className={`p-4 rounded-lg border cursor-pointer transition-all duration-150 ${isDark ? 'bg-[#152040] border-[#253660] hover:bg-[#1E3058] hover:border-[#3A5585]' : 'bg-white border-gray-100 hover:bg-gray-100 hover:border-gray-200'}`} data-id="element-1135">
                  <div className="flex items-start justify-between gap-4" data-id="element-1136">
                    <div className="flex-1 min-w-0" data-id="element-1137">
                      <div className="flex items-center gap-2 mb-2" data-id="element-1138">
                        <h3 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-1139">
                          {task.studentName || 'Unknown Student'}
                        </h3>
                        {getTaskStatusBadge(task, isDark)}
                      </div>
                      <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-1140">
                        {task.title}
                      </p>
                      {task.dueDate || task.deadline || task.dueAt ? <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1141">
                          Practice:{' '}
                          {formatDueDate(task.dueDate || task.deadline || task.dueAt)}
                        </p> : null}
                      {task.notes && task.notes.trim() && <p className={`text-xs mt-1 truncate flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1142">
                          <FileTextIcon className="h-3 w-3 flex-shrink-0" data-id="element-1143" />
                          {task.notes.trim()}
                        </p>}
                    </div>

                    {/* 3-dot menu */}
                    <div className="relative flex-shrink-0" data-id="element-1144">
                      <button data-menu-button onClick={e => toggleTaskMenu(task.id, e)} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-600 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`} aria-label="Task actions" data-id="element-1145">
                        <MoreVerticalIcon className="h-5 w-5" data-id="element-1146" />
                      </button>

                      {/* Dropdown menu - absolutely positioned within card */}
                      {openMenuTaskId === task.id && <div data-menu-dropdown className={`absolute top-full right-0 mt-1 w-56 rounded-lg border z-50 ${isDark ? 'border-navy-600 bg-navy-800' : 'border-gray-200 bg-white'}`} onClick={e => e.stopPropagation()} style={{
                  boxShadow: isDark ? '0 10px 30px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(75, 85, 99, 0.8)' : '0 10px 30px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.1)'
                }} data-id="element-1147">
                          <button onClick={e => handleMarkComplete(task, e)} className={`w-full flex items-center px-4 py-3 text-sm transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-1148">
                            <CheckIcon className="h-4 w-4 mr-3" data-id="element-1149" />
                            Mark as Complete
                          </button>
                          <button onClick={e => handleDeleteTaskClick(task, e)} className={`w-full flex items-center px-4 py-3 text-sm transition-colors text-red-500 ${isDark ? 'hover:bg-navy-700' : 'hover:bg-gray-50'}`} data-id="element-1150">
                            <Trash2Icon className="h-4 w-4 mr-3" data-id="element-1151" />
                            Delete Task
                          </button>
                        </div>}
                    </div>
                  </div>
                </div>)}
            </div>}
        </div>
      </div>

      {/* Task Details Panel */}
      <TaskDetailsPanel open={isTaskPanelOpen} task={selectedTaskForPanel} onClose={closeTaskPanel} onEditClick={handleTaskEditClick} onExitEditMode={handleTaskExitEditMode} onDeleteClick={handleTaskDelete} onSaveEdit={handleTaskSave} darkMode={isDark} isEditMode={isTaskEditMode} teacherId={currentUser?.uid} data-id="element-1152" />

      {/* Delete Task Confirmation Modal */}
      <DeleteTaskModal isOpen={isDeleteModalOpen} onClose={() => {
      setIsDeleteModalOpen(false);
      setTaskToDelete(null);
    }} onConfirm={handleConfirmDelete} taskTitle={taskToDelete?.title || 'Task'} studentName={taskToDelete?.studentName} darkMode={isDark} data-id="element-1153" />
    </>;
};